import { useEffect, useRef, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import BlockShell from "./BlockShell";
import { absUrl } from "./media";
import { renderInline } from "./StoryChapterBlock";
import { usePlayer } from "../../player/PlayerContext";
import { Link } from "react-router-dom";
import { usePageOwner } from "../../hooks/usePageOwner";
import { useArtistFocus } from "../../hooks/useArtistFocus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "–:––");

// An artist's HOARD tracks as a playlist. Playback runs through the site-wide
// floating player, so the music keeps going while people scroll — and when they leave the page.
const TrovePlaylistBlock = ({ block }) => {
  const cfg = block.config || {};
  const accent = cfg.accent || "#B8860B";
  const [tracks, setTracks] = useState([]);
  const strip = useRef(null);
  const p = usePlayer();

  useEffect(() => {
    if (!cfg.artist_slug) return;
    axios.get(`${API}/trove`, { params: { item_type: "music", artist_slug: cfg.artist_slug } })
      .then((r) => setTracks((r.data || []).filter((x) => x.file_url).slice(0, Number(cfg.limit) || 40)))
      .catch(() => setTracks([]));
  }, [cfg.artist_slug, cfg.limit]);

  const { isOwner, verified } = usePageOwner(cfg.artist_slug);
  useArtistFocus(cfg.artist_slug);
  const addSong = isOwner ? (
    <Link to="/contribute/upload?type=musician" className="inline-flex items-center gap-2 rounded-full px-5 py-3 min-h-[48px] text-sm font-extrabold text-white no-underline hover:opacity-90" style={{ backgroundColor: accent }} data-testid="owner-add-song-cta">
      <Icons.Plus className="h-4 w-4" /> Add a song — feature your new music{verified ? "" : " (verify your email first)"}
    </Link>
  ) : null;
  if (!cfg.artist_slug) return null;
  if (!tracks.length) {
    return isOwner ? (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="fest-trove-playlist-empty">
        <BlockShell block={block} accent={accent}>
          <div className="rounded-2xl border border-dashed border-[#E2E0DC] p-6 text-center space-y-3">
            <p className="text-base text-[#5C5C5C]">No tracks in THE HOARD yet. Your MP3s stream on every page of THE WALL and fans can ♥ + download them.</p>
            {addSong}
          </div>
        </BlockShell>
      </section>
    ) : null;
  }
  const cover = (x) => (x?.album_art_url ? absUrl(x.album_art_url) : cfg.cover ? absUrl(cfg.cover) : "");
  const queue = tracks.map((x) => ({ ...x, artist_name: x.artist_name || x.contributor_name }));
  const play = (i) => (p.current?.item_id === tracks[i].item_id ? p.toggle() : p.play(queue[i]));

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="fest-trove-playlist">
      <BlockShell block={block} accent={accent}>
        {cfg.intro && <p className="text-base md:text-lg text-[#3a3a3a] leading-relaxed max-w-3xl mb-5">{renderInline(cfg.intro, accent)}</p>}
        <div className="relative">
          <div ref={strip} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-6 px-6 md:mx-0 md:px-0 no-scrollbar" data-testid="trove-tracklist">
            {tracks.map((x, i) => {
              const active = p.current?.item_id === x.item_id;
              const liked = p.isLiked(x.item_id);
              return (
                <div key={x.item_id} className={`group snap-start shrink-0 w-40 sm:w-44 text-left rounded-2xl border bg-white/80 p-2.5 transition-all hover:-translate-y-0.5 hover:shadow-lg ${active ? "border-[#B8860B] shadow-md" : "border-[#E2E0DC]"}`} data-testid={`trove-track-${i}`}>
                  <button type="button" onClick={() => play(i)} className="relative block w-full aspect-square rounded-xl overflow-hidden bg-[#1A1A1A]" aria-label={active && p.playing ? "Pause" : "Play"}>
                    {cover(x) && <img src={cover(x)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />}
                    <span className="absolute inset-0 grid place-items-center bg-black/25 opacity-90 group-hover:opacity-100 transition-opacity">
                      <span className="grid h-11 w-11 place-items-center rounded-full text-white shadow-lg" style={{ backgroundColor: active ? accent : "rgba(0,0,0,0.6)" }}>
                        {active && p.playing ? <Icons.Pause className="h-5 w-5" /> : <Icons.Play className="h-5 w-5 ml-0.5" />}
                      </span>
                    </span>
                    {active && <span className="absolute bottom-1.5 left-1.5 rounded-full bg-black/70 px-2 py-0.5 font-mono text-[10px] text-white">{fmt(p.time)} / {fmt(p.duration)}</span>}
                  </button>
                  <div className="mt-2 flex items-start gap-1">
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-[#1A1A1A] leading-tight line-clamp-2">{x.title}</span>
                      <span className="block text-[11px] text-[#5C5C5C] truncate">{x.album || x.artist_name}</span>
                    </span>
                    <button type="button" onClick={() => p.toggleLike(queue[i])} className={`p-1 rounded-full hover:bg-black/5 ${liked ? "text-rose-500" : "text-[#9a9a9a]"}`} aria-label={liked ? "Remove from playlist" : "Add to playlist"} data-testid={`trove-track-like-${i}`}>
                      <Icons.Heart className={`h-4 w-4 ${liked ? "fill-current" : ""}`} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={() => strip.current?.scrollBy({ left: -480, behavior: "smooth" })} className="hidden md:grid absolute -left-4 top-1/3 h-9 w-9 place-items-center rounded-full bg-white shadow ring-1 ring-black/10 hover:bg-[#F9F8F6]" aria-label="Scroll left" data-testid="trove-scroll-left"><Icons.ChevronLeft className="h-5 w-5" /></button>
          <button type="button" onClick={() => strip.current?.scrollBy({ left: 480, behavior: "smooth" })} className="hidden md:grid absolute -right-4 top-1/3 h-9 w-9 place-items-center rounded-full bg-white shadow ring-1 ring-black/10 hover:bg-[#F9F8F6]" aria-label="Scroll right" data-testid="trove-scroll-right"><Icons.ChevronRight className="h-5 w-5" /></button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="text-xs text-[#5C5C5C]">{tracks.length} tracks · swipe or use the arrows · then the whole HOARD plays on random · ♥ saves to Your Playlist</span>
          {cfg.trove_link && <a href={cfg.trove_link} className="text-sm font-bold text-[#1A1A1A] underline-offset-2 hover:underline" data-testid="trove-link">{cfg.trove_link_label || "Get everything in THE HOARD →"}</a>}
        </div>
        {addSong && <div className="mt-4">{addSong}</div>}
      </BlockShell>
    </section>
  );
};

export default TrovePlaylistBlock;
